import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit as limitQuery,
  Timestamp,
} from 'firebase/firestore';
import { getDB } from '../config/firebase';
import { Subject, SubjectFormData, PaginatedResponse } from '../types';
import { ClassService } from './classService';
import { safeTimestampToDate } from '../utils/timestampUtils';

export class SubjectService {
  private static collectionName = 'subjects';

  // Subject CRUD operations
  static async getSubjects(limit: number = 50, page: number = 1): Promise<PaginatedResponse<Subject>> {
    try {
      const db = getDB();
      const q = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc'),
        limitQuery(limit)
      );

      const querySnapshot = await getDocs(q);
      const subjects: Subject[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        const subject = {
          id: doc.id,
          name: data.name,
          code: data.code,
          description: data.description,
          credits: data.credits,
          department: data.department,
          semester: data.semester,
          classIds: data.classIds || [],
          students: data.students || [],
          isActive: data.isActive ?? true,
          createdAt: safeTimestampToDate(data.createdAt),
          updatedAt: safeTimestampToDate(data.updatedAt),
        };
        
        // Only include active subjects
        if (subject.isActive) {
          subjects.push(subject);
        }
      });

      return {
        data: subjects,
        total: subjects.length,
        page,
        limit,
        totalPages: Math.ceil(subjects.length / limit),
      };
    } catch (error) {
      console.error('Error fetching subjects:', error);
      throw new Error('Failed to fetch subjects');
    }
  }

  static async getSubjectById(id: string): Promise<Subject | null> {
    try {
      const db = getDB();
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();

        return {
          id: docSnap.id,
          name: data.name,
          code: data.code,
          description: data.description,
          credits: data.credits,
          department: data.department,
          semester: data.semester,
          classIds: data.classIds || [],
          students: data.students || [],
          isActive: data.isActive ?? true,
          createdAt: safeTimestampToDate(data.createdAt),
          updatedAt: safeTimestampToDate(data.updatedAt),
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching subject:', error);
      return null;
    }
  }

  static async createSubject(data: SubjectFormData): Promise<Subject> {
    try {
      const db = getDB();
      
      // Check if subject code already exists
      const existingSubject = await this.getSubjectByCode(data.code);
      if (existingSubject) {
        throw new Error('Subject code already exists');
      }

      // Clone students from selected classes
      let clonedStudents: any[] = [];
      if (data.classIds && data.classIds.length > 0) {
        const allClasses = await ClassService.getAllClasses();
        for (const classId of data.classIds) {
          const classData = allClasses.find((c: any) => c.id === classId);
          if (classData && classData.students) {
            // Clone each student with their class info
            const classStudents = classData.students.map((student: any) => ({
              ...student,
              originalClassId: classId,
              originalClassName: classData.name,
              enrolledAt: new Date(),
            }));
            clonedStudents.push(...classStudents);
          }
        }
      }

      const subjectData = {
        ...data,
        students: clonedStudents,
        isActive: true,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, this.collectionName), subjectData);
      
      return {
        id: docRef.id,
        ...data,
        students: clonedStudents,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    } catch (error) {
      console.error('Error creating subject:', error);
      throw error;
    }
  }

  private static async getSubjectByCode(code: string): Promise<Subject | null> {
    try {
      const db = getDB();
      const q = query(
        collection(db, this.collectionName),
        where('code', '==', code),
        limitQuery(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();

        return {
          id: doc.id,
          name: data.name,
          code: data.code,
          description: data.description,
          credits: data.credits,
          department: data.department,
          semester: data.semester,
          classIds: data.classIds || [],
          students: data.students || [],
          isActive: data.isActive ?? true,
          createdAt: safeTimestampToDate(data.createdAt),
          updatedAt: safeTimestampToDate(data.updatedAt),
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching subject by code:', error);
      return null;
    }
  }

  static async updateSubject(id: string, data: Partial<SubjectFormData>): Promise<Subject> {
    try {
      const db = getDB();
      const docRef = doc(db, this.collectionName, id);
      
      // Check if subject exists
      const existingDoc = await getDoc(docRef);
      if (!existingDoc.exists()) {
        throw new Error('Subject not found');
      }

      const existingData = existingDoc.data();

      // Check if updating to an existing subject code
      if (data.code) {
        const existingSubject = await this.getSubjectByCode(data.code);
        if (existingSubject && existingSubject.id !== id) {
          throw new Error('Subject code already exists');
        }
      }

      // Handle class changes by re-cloning students
      if (data.classIds !== undefined) {
        // Clone students from new selected classes
        let clonedStudents: any[] = [];
        if (data.classIds.length > 0) {
          const allClasses = await ClassService.getAllClasses();
          for (const classId of data.classIds) {
            const classData = allClasses.find((c: any) => c.id === classId);
            if (classData && classData.students) {
              // Clone each student with their class info
              const classStudents = classData.students.map((student: any) => ({
                ...student,
                originalClassId: classId,
                originalClassName: classData.name,
                enrolledAt: new Date(),
              }));
              clonedStudents.push(...classStudents);
            }
          }
        }
        data.students = clonedStudents;
      }

      const updateData = {
        ...data,
        updatedAt: Timestamp.fromDate(new Date()),
      };

      await updateDoc(docRef, updateData);
      
      const updatedDoc = await getDoc(docRef);
      const updatedData = updatedDoc.data();
      
      return {
        id: updatedDoc.id,
        name: updatedData!.name,
        code: updatedData!.code,
        description: updatedData!.description,
        credits: updatedData!.credits,
        department: updatedData!.department,
        semester: updatedData!.semester,
        classIds: updatedData!.classIds || [],
        students: updatedData!.students || [],
        isActive: updatedData!.isActive ?? true,
        createdAt: safeTimestampToDate(updatedData!.createdAt),
        updatedAt: safeTimestampToDate(updatedData!.updatedAt),
      };
    } catch (error) {
      console.error('Error updating subject:', error);
      throw error;
    }
  }

  static async deleteSubject(id: string): Promise<void> {
    try {
      const db = getDB();
      const docRef = doc(db, this.collectionName, id);
      
      // Check if subject exists
      const existingDoc = await getDoc(docRef);
      if (!existingDoc.exists()) {
        throw new Error('Subject not found');
      }

      // TODO: Check if subject is assigned to any faculty
      // For now, we'll just delete it
      
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting subject:', error);
      throw error;
    }
  }

  static async toggleSubjectStatus(id: string): Promise<Subject> {
    try {
      const db = getDB();
      const docRef = doc(db, this.collectionName, id);
      
      const existingDoc = await getDoc(docRef);
      if (!existingDoc.exists()) {
        throw new Error('Subject not found');
      }

      const currentData = existingDoc.data();
      const newStatus = !currentData!.isActive;

      await updateDoc(docRef, {
        isActive: newStatus,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      return {
        id: existingDoc.id,
        name: currentData!.name,
        code: currentData!.code,
        description: currentData!.description,
        credits: currentData!.credits,
        department: currentData!.department,
        semester: currentData!.semester,
        classIds: currentData!.classIds || [],
        students: currentData!.students || [],
        isActive: newStatus,
        createdAt: safeTimestampToDate(currentData!.createdAt),
        updatedAt: new Date(),
      };
    } catch (error) {
      console.error('Error toggling subject status:', error);
      throw error;
    }
  }

  static async searchSubjects(searchQuery: string): Promise<Subject[]> {
    try {
      const db = getDB();
      const q = query(
        collection(db, this.collectionName),
        orderBy('name'),
        limitQuery(50)
      );

      const querySnapshot = await getDocs(q);
      const subjects: Subject[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const subject: Subject = {
          id: doc.id,
          name: data.name,
          code: data.code,
          description: data.description,
          credits: data.credits,
          department: data.department,
          semester: data.semester,
          classIds: data.classIds || [],
          students: data.students || [],
          isActive: data.isActive ?? true,
          createdAt: safeTimestampToDate(data.createdAt),
          updatedAt: safeTimestampToDate(data.updatedAt),
        };

        // Client-side filtering (Firestore doesn't support complex text search)
        const query = searchQuery.toLowerCase();
        if (
          subject.isActive &&
          (subject.name.toLowerCase().includes(query) ||
          subject.code.toLowerCase().includes(query) ||
          subject.department?.toLowerCase().includes(query))
        ) {
          subjects.push(subject);
        }
      });

      return subjects;
    } catch (error) {
      console.error('Error searching subjects:', error);
      return [];
    }
  }

  static async getSubjectsByDepartment(department: string): Promise<Subject[]> {
    try {
      const db = getDB();
      const q = query(
        collection(db, this.collectionName),
        where('department', '==', department),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      const subjects: Subject[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const subject = {
          id: doc.id,
          name: data.name,
          code: data.code,
          description: data.description,
          credits: data.credits,
          department: data.department,
          semester: data.semester,
          classIds: data.classIds || [],
          students: data.students || [],
          isActive: data.isActive ?? true,
          createdAt: safeTimestampToDate(data.createdAt),
          updatedAt: safeTimestampToDate(data.updatedAt),
        };
        
        // Only include active subjects
        if (subject.isActive) {
          subjects.push(subject);
        }
      });

      return subjects;
    } catch (error) {
      console.error('Error fetching subjects by department:', error);
      return [];
    }
  }

  static async getSubjectsBySemester(semester: number): Promise<Subject[]> {
    try {
      const db = getDB();
      const q = query(
        collection(db, this.collectionName),
        where('semester', '==', semester),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      const subjects: Subject[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const subject = {
          id: doc.id,
          name: data.name,
          code: data.code,
          description: data.description,
          credits: data.credits,
          department: data.department,
          semester: data.semester,
          classIds: data.classIds || [],
          students: data.students || [],
          isActive: data.isActive ?? true,
          createdAt: safeTimestampToDate(data.createdAt),
          updatedAt: safeTimestampToDate(data.updatedAt),
        };
        
        // Only include active subjects
        if (subject.isActive) {
          subjects.push(subject);
        }
      });

      return subjects;
    } catch (error) {
      console.error('Error fetching subjects by semester:', error);
      return [];
    }
  }

  static async getDepartments(): Promise<string[]> {
    try {
      const db = getDB();
      const q = query(collection(db, this.collectionName));
      const querySnapshot = await getDocs(q);
      
      const departments = new Set<string>();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.department) {
          departments.add(data.department);
        }
      });
      
      return Array.from(departments).sort();
    } catch (error) {
      console.error('Error fetching departments:', error);
      return [];
    }
  }

  static async getAcademicYears(): Promise<string[]> {
    // Return static academic years for now
    const currentYear = new Date().getFullYear();
    return [
      `${currentYear - 1}-${(currentYear).toString().slice(-2)}`,
      `${currentYear}-${(currentYear + 1).toString().slice(-2)}`,
      `${currentYear + 1}-${(currentYear + 2).toString().slice(-2)}`,
    ];
  }
} 